import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import CardActions from "@/components/base/CardActions";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import {
  AGENCE_BIEN_TYPE_LABELS,
  AGENCE_MODE_PAIEMENT_LABELS,
  AGENCE_MODE_PAIEMENT_OPTIONS,
  agenceContratReference,
  agenceLocataireNomComplet,
  agencePaiementReference,
  type AgencePaiement,
} from "@/pages/agence/data/types";
import { monthLabel } from "@/pages/agence/loyers/periods";
import { useProprietaires } from "@/pages/agence/proprietaires/ProprietairesProvider";
import { proprietaireNomComplet } from "@/pages/agence/proprietaires/types";
import { useEmployes } from "@/pages/agence/employes/EmployesProvider";
import type { ExportColumn } from "@/utils/export";
import { formatDate, formatFcfa } from "@/utils/format";
import PaiementForm from "./components/PaiementForm";
import RecuModal from "./components/RecuModal";

type SortKey = "recent" | "ancien" | "montant";

const sortOptions = [
  { value: "recent", label: "Plus récents" },
  { value: "ancien", label: "Plus anciens" },
  { value: "montant", label: "Montant décroissant" },
];

export default function AgencePaiementsPage() {
  const { paiements, statsPaiements, contrats, immeubles, getContrat, getBien, getImmeuble, getLocataire, removePaiement } =
    useAgenceData();
  const { proprietaires } = useProprietaires();
  const { can } = useEmployes();
  const [searchParams] = useSearchParams();

  const contratParam = searchParams.get("contrat") ?? "";
  const periodeParam = searchParams.get("periode") ?? "";
  const proprietaireParam = searchParams.get("proprietaire") ?? "";
  const immeubleParam = searchParams.get("immeuble") ?? "";
  const bienParam = searchParams.get("bien") ?? "";
  const locataireParam = searchParams.get("locataire") ?? "";
  const openFromQuery = searchParams.get("nouveau") === "1";

  const [formOpen, setFormOpen] = useState(openFromQuery);
  const [receipt, setReceipt] = useState<AgencePaiement | null>(null);
  const [search, setSearch] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [proprietaireFilter, setProprietaireFilter] = useState(proprietaireParam);
  const [immeubleFilter, setImmeubleFilter] = useState(immeubleParam);
  const [periodeFilter, setPeriodeFilter] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");
  const [toDelete, setToDelete] = useState<AgencePaiement | null>(null);

  const hasPaiements = paiements.length > 0;
  const hasContrats = contrats.length > 0;

  const periodes = useMemo(
    () => Array.from(new Set(paiements.map((item) => item.periode))).sort((a, b) => (a < b ? 1 : -1)),
    [paiements]
  );

  const modeOptions = useMemo(() => [{ value: "", label: "Tous les modes" }, ...AGENCE_MODE_PAIEMENT_OPTIONS], []);

  const proprietaireOptions = useMemo(
    () => [
      { value: "", label: "Tous les propriétaires" },
      ...proprietaires.map((item) => ({ value: item.id, label: proprietaireNomComplet(item) })),
    ],
    [proprietaires]
  );

  const immeubleOptions = useMemo(
    () => [
      { value: "", label: "Tous les immeubles" },
      ...immeubles
        .filter((immeuble) => !proprietaireFilter || immeuble.proprietaireId === proprietaireFilter)
        .map((immeuble) => ({ value: immeuble.id, label: immeuble.nom })),
    ],
    [immeubles, proprietaireFilter]
  );

  const periodeOptions = useMemo(
    () => [{ value: "", label: "Toutes les périodes" }, ...periodes.map((p) => ({ value: p, label: monthLabel(p) }))],
    [periodes]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = paiements.filter((paiement) => {
      const contrat = getContrat(paiement.contratId);
      const matchesMode = !modeFilter || paiement.mode === modeFilter;
      const matchesPeriode = !periodeFilter || paiement.periode === periodeFilter;
      const matchesProprietaire = !proprietaireFilter || contrat?.proprietaireId === proprietaireFilter;
      const matchesImmeuble = !immeubleFilter || contrat?.immeubleId === immeubleFilter;
      const matchesContrat = !contratParam || paiement.contratId === contratParam;

      const bien = contrat ? getBien(contrat.bienId) : undefined;
      const locataire = contrat ? getLocataire(contrat.locataireId) : undefined;
      const immeuble = immeubles.find((item) => item.id === contrat?.immeubleId);
      const matchesSearch =
        !term ||
        [
          contrat ? agenceContratReference(contrat.id) : "",
          bien ? AGENCE_BIEN_TYPE_LABELS[bien.type] : "",
          bien?.reference ?? "",
          locataire ? agenceLocataireNomComplet(locataire) : "",
          immeuble?.nom ?? "",
          AGENCE_MODE_PAIEMENT_LABELS[paiement.mode],
          paiement.reference,
        ]
          .join(" ")
          .toLowerCase()
          .includes(term);

      return matchesMode && matchesPeriode && matchesProprietaire && matchesImmeuble && matchesContrat && matchesSearch;
    });

    return result.sort((a, b) => {
      if (sort === "montant") return b.montant - a.montant;
      if (sort === "ancien") return new Date(a.date).getTime() - new Date(b.date).getTime();
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [
    paiements,
    search,
    modeFilter,
    periodeFilter,
    proprietaireFilter,
    immeubleFilter,
    contratParam,
    sort,
    getContrat,
    getBien,
    getLocataire,
    immeubles,
  ]);

  const isFiltering =
    Boolean(search.trim()) ||
    Boolean(modeFilter) ||
    Boolean(periodeFilter) ||
    Boolean(proprietaireFilter) ||
    Boolean(immeubleFilter) ||
    Boolean(contratParam);

  const resetFilters = () => {
    setSearch("");
    setModeFilter("");
    setPeriodeFilter("");
    setProprietaireFilter("");
    setImmeubleFilter("");
  };

  const exportColumns: ExportColumn<AgencePaiement>[] = [
    { header: "Référence", value: (paiement) => agencePaiementReference(paiement.id) },
    {
      header: "Contrat",
      value: (paiement) => {
        const contrat = getContrat(paiement.contratId);
        return contrat ? agenceContratReference(contrat.id) : "";
      },
    },
    {
      header: "Propriétaire",
      value: (paiement) => {
        const contrat = getContrat(paiement.contratId);
        const owner = contrat ? proprietaires.find((item) => item.id === contrat.proprietaireId) : undefined;
        return owner ? proprietaireNomComplet(owner) : "";
      },
    },
    {
      header: "Immeuble",
      value: (paiement) => getImmeuble(getContrat(paiement.contratId)?.immeubleId ?? "")?.nom ?? "",
    },
    {
      header: "Bien",
      value: (paiement) => {
        const contrat = getContrat(paiement.contratId);
        const bien = contrat ? getBien(contrat.bienId) : undefined;
        return bien ? `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` ${bien.reference}` : ""}` : "";
      },
    },
    {
      header: "Locataire",
      value: (paiement) => {
        const contrat = getContrat(paiement.contratId);
        const locataire = contrat ? getLocataire(contrat.locataireId) : undefined;
        return locataire ? agenceLocataireNomComplet(locataire) : "";
      },
    },
    { header: "Période", value: (paiement) => monthLabel(paiement.periode) },
    { header: "Montant (FCFA)", value: (paiement) => formatFcfa(paiement.montant) },
    { header: "Date", value: (paiement) => formatDate(paiement.date) },
    { header: "Mode de paiement", value: (paiement) => AGENCE_MODE_PAIEMENT_LABELS[paiement.mode] },
  ];

  const columns: DataTableColumn<AgencePaiement>[] = [
    {
      key: "reference",
      header: "Référence",
      className: "min-w-[130px]",
      render: (paiement) => (
        <span className="font-label font-semibold text-foreground-950">{agencePaiementReference(paiement.id)}</span>
      ),
    },
    {
      key: "locataire",
      header: "Locataire",
      className: "min-w-[180px]",
      render: (paiement) => {
        const contrat = getContrat(paiement.contratId);
        const locataire = contrat ? getLocataire(contrat.locataireId) : undefined;
        return <span className="text-foreground-700">{locataire ? agenceLocataireNomComplet(locataire) : "Non renseigné"}</span>;
      },
    },
    {
      key: "bien",
      header: "Bien · Immeuble",
      className: "min-w-[200px]",
      render: (paiement) => {
        const contrat = getContrat(paiement.contratId);
        const bien = contrat ? getBien(contrat.bienId) : undefined;
        const immeuble = contrat ? getImmeuble(contrat.immeubleId) : undefined;
        return (
          <div className="flex flex-col">
            <span className="truncate text-foreground-800">{bien ? AGENCE_BIEN_TYPE_LABELS[bien.type] : "Bien non renseigné"}</span>
            <span className="truncate text-xs text-foreground-500">{immeuble ? immeuble.nom : "Immeuble non renseigné"}</span>
          </div>
        );
      },
    },
    {
      key: "periode",
      header: "Période",
      className: "min-w-[130px]",
      render: (paiement) => <span className="text-foreground-700">{monthLabel(paiement.periode)}</span>,
    },
    {
      key: "montant",
      header: "Montant",
      className: "min-w-[130px]",
      render: (paiement) => (
        <span className="font-label font-medium text-foreground-950">{formatFcfa(paiement.montant)}</span>
      ),
    },
    {
      key: "mode",
      header: "Mode",
      className: "min-w-[140px]",
      render: (paiement) => <span className="text-foreground-700">{AGENCE_MODE_PAIEMENT_LABELS[paiement.mode]}</span>,
    },
    {
      key: "statut",
      header: "Statut",
      render: () => (
        <Badge tone="secondary" icon="ri-checkbox-circle-line">
          Encaissé
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[210px]",
      render: (paiement) => (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setReceipt(paiement)}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-receipt-line text-base" aria-hidden="true" />
            Voir le reçu
          </button>
          <CardActions onDelete={can("paiements.supprimer") ? () => setToDelete(paiement) : undefined} />
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Paiements</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Enregistrez les encaissements de loyers (Espèces, Orange Money, Moov Money, virement, chèque) et générez un
            reçu. Chaque paiement met à jour le statut du loyer concerné.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <ExportMenu rows={filtered} columns={exportColumns} filename="agence-paiements" title="Paiements" />
          {hasContrats ? (
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Enregistrer un paiement
            </button>
          ) : (
            <Link
              to="/agence/contrats"
              className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-file-text-line text-base" aria-hidden="true" />
              Préparer un contrat
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-hand-coin-line" label="Paiements" value={String(statsPaiements.total)} tone="primary" />
        <StatCard
          icon="ri-money-cny-circle-line"
          label="Montant encaissé"
          value={formatFcfa(statsPaiements.montantTotal)}
          tone="secondary"
        />
        <StatCard
          icon="ri-calendar-check-line"
          label="Encaissé ce mois"
          value={formatFcfa(statsPaiements.ceMois)}
          tone="accent"
        />
        <StatCard
          icon="ri-apps-2-line"
          label="Modes de paiement"
          value={String(AGENCE_MODE_PAIEMENT_OPTIONS.length)}
          hint="Espèces, Mobile Money, virement..."
          tone="neutral"
        />
      </div>

      {hasPaiements && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
            <div className="flex flex-col gap-1.5 lg:col-span-2">
              <label htmlFor="agence-pay-search" className="text-xs font-label font-medium text-foreground-700">
                Recherche
              </label>
              <Input
                id="agence-pay-search"
                icon="ri-search-line"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Référence, contrat, locataire, immeuble..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-pay-mode-filter" className="text-xs font-label font-medium text-foreground-700">
                Mode de paiement
              </label>
              <Select
                id="agence-pay-mode-filter"
                value={modeFilter}
                onChange={(event) => setModeFilter(event.target.value)}
                options={modeOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-pay-periode-filter" className="text-xs font-label font-medium text-foreground-700">
                Période
              </label>
              <Select
                id="agence-pay-periode-filter"
                value={periodeFilter}
                onChange={(event) => setPeriodeFilter(event.target.value)}
                options={periodeOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5 lg:col-span-2">
              <label htmlFor="agence-pay-proprietaire-filter" className="text-xs font-label font-medium text-foreground-700">
                Propriétaire
              </label>
              <Select
                id="agence-pay-proprietaire-filter"
                value={proprietaireFilter}
                onChange={(event) => {
                  setProprietaireFilter(event.target.value);
                  setImmeubleFilter("");
                }}
                options={proprietaireOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-pay-immeuble-filter" className="text-xs font-label font-medium text-foreground-700">
                Immeuble
              </label>
              <Select
                id="agence-pay-immeuble-filter"
                value={immeubleFilter}
                onChange={(event) => setImmeubleFilter(event.target.value)}
                options={immeubleOptions}
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 border-t border-background-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-foreground-500">
              {filtered.length} paiement{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur{" "}
              {paiements.length}
            </p>
            <div className="flex items-center gap-3">
              {isFiltering && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
                >
                  <i className="ri-refresh-line text-sm" aria-hidden="true" />
                  Réinitialiser
                </button>
              )}
              <div className="w-44">
                <Select
                  aria-label="Trier par"
                  value={sort}
                  onChange={(event) => setSort(event.target.value as SortKey)}
                  options={sortOptions}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {!hasContrats && (
        <EmptyState
          icon="ri-hand-coin-line"
          title="Aucun paiement possible"
          description="Les paiements se rattachent à un contrat. Créez d'abord un contrat pour pouvoir enregistrer un encaissement."
          action={
            <Link
              to="/agence/contrats"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-file-text-line text-base" aria-hidden="true" />
              Créer un contrat
            </Link>
          }
        />
      )}

      {hasContrats && !hasPaiements && (
        <EmptyState
          icon="ri-hand-coin-line"
          title="Aucun paiement enregistré"
          description="Enregistrez votre premier encaissement en choisissant le propriétaire, le locataire, le contrat et la période concernée."
          action={
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Enregistrer un paiement
            </button>
          }
        />
      )}

      {hasPaiements && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun paiement ne correspond à votre recherche ou aux filtres sélectionnés. Essayez de modifier vos critères."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(paiement) => paiement.id}
          minWidthClass="min-w-[1180px]"
          caption="Paiements"
        />
      )}

      <PaiementForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onCreated={(paiement) => setReceipt(paiement)}
        defaultContratId={contratParam || undefined}
        defaultPeriode={periodeParam || undefined}
        defaultProprietaireId={proprietaireParam || undefined}
        defaultImmeubleId={immeubleParam || undefined}
        defaultBienId={bienParam || undefined}
        defaultLocataireId={locataireParam || undefined}
      />

      <RecuModal paiement={receipt} onClose={() => setReceipt(null)} />

      <ConfirmDeleteModal
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          if (toDelete) removePaiement(toDelete.id);
          setToDelete(null);
        }}
        title="Supprimer le paiement"
        description={
          toDelete
            ? `Voulez-vous vraiment supprimer le paiement de ${formatFcfa(toDelete.montant)} ? Le statut du loyer concerné sera recalculé.`
            : ""
        }
      />
    </div>
  );
}